import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Alert,
  Button,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3100/api/v1';

type SupplyStatus = 'excellent' | 'good' | 'adequate' | 'low' | 'critical' | string;

interface Supply {
  id?: string | number;
  name?: string;
  item?: string;
  status: SupplyStatus;
  stock?: string | number;
  days?: number;
}

const SupplyChainStatus: React.FC = () => {
  const [supplies, setSupplies] = useState<Supply[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      const headers: Record<string, string> = { 'Content-Type': 'application/json' };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const resp = await fetch(`${API_BASE}/kitchen-ops/supply-chain`, { headers });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      const list = data.data ?? data;
      setSupplies(Array.isArray(list) ? list : []);
    } catch (e) {
      setError((e as Error).message || 'Failed to load supply chain data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const getStatusText = (status: SupplyStatus, days?: number): string => {
    const daysStr = days != null ? ` (${days} days)` : '';
    switch (status) {
      case 'excellent': return `Excellent${daysStr}`;
      case 'good':      return `Good${daysStr}`;
      case 'adequate':  return `Adequate${daysStr}`;
      case 'low':       return `Low${daysStr}`;
      case 'critical':  return `Critical${daysStr}`;
      default:          return days != null ? `${days} days` : status;
    }
  };

  const getSupplyItemStyles = (status: SupplyStatus) => {
    let backgroundColor = '#e8f5e8';
    let borderColor = '#28a745';
    if (status === 'low') { backgroundColor = '#fff3cd'; borderColor = '#ffc107'; }
    else if (status === 'critical') { backgroundColor = '#f8d7da'; borderColor = '#dc3545'; }
    return { backgroundColor, p: 2, borderRadius: 2, borderLeft: `4px solid ${borderColor}` };
  };

  const getStatusColor = (status: SupplyStatus): string => {
    if (status === 'low') return '#856404';
    if (status === 'critical') return '#721c24';
    return '#28a745';
  };

  const getStatusIcon = (status: SupplyStatus): React.ReactElement => {
    if (status === 'critical') return <ErrorIcon sx={{ color: '#dc3545', mr: 0.5 }} fontSize="small" />;
    if (status === 'low') return <WarningIcon sx={{ color: '#ffc107', mr: 0.5 }} fontSize="small" />;
    return <CheckCircleIcon sx={{ color: '#28a745', mr: 0.5 }} fontSize="small" />;
  };

  return (
    <Card sx={{ boxShadow: '0 10px 30px rgba(0,0,0,0.15)', borderRadius: 3 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h6">
            Supply Chain Status
          </Typography>
          <Button size="small" startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
            Refresh
          </Button>
        </Box>

        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : supplies.length === 0 ? (
          <Alert severity="info">No supply chain data available from the API.</Alert>
        ) : (
          <Grid container spacing={2}>
            {supplies.map((supply, idx) => (
              <Grid item xs={12} sm={6} md={4} key={supply.id ?? idx}>
                <Box sx={getSupplyItemStyles(supply.status)}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                    {supply.name ?? supply.item ?? '--'}
                  </Typography>
                  {supply.stock != null && (
                    <Typography variant="body2" sx={{ mb: 0.5 }}>
                      Stock: {supply.stock}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {getStatusIcon(supply.status)}
                    <Typography variant="body2" sx={{ color: getStatusColor(supply.status) }}>
                      Status: {getStatusText(supply.status, supply.days)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        )}
      </CardContent>
    </Card>
  );
};

export default SupplyChainStatus;
